import { useState } from 'react';
import { Link, Navigate } from 'react-router';
import { Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@eriko/ui/src/components/ui/button';
import { Input } from '@eriko/ui/src/components/ui/input';
import { Label } from '@eriko/ui/src/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@eriko/ui/src/components/ui/select';
import { registerSchema } from '@eriko/validation';
import { useRegister } from '../../hooks/useAuth';

export function RegisterPage() {
  const { t } = useTranslation('auth');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [orgName, setOrgName] = useState('');
  const [jurisdiction, setJurisdiction] = useState<'US' | 'UK'>('US');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const register = useRegister();

  // Redirect if already logged in
  if (localStorage.getItem('eriko_access_token')) return <Navigate to="/" replace />;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrors({});

    const result = registerSchema.safeParse({ email, password, fullName, orgName, jurisdiction });
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of result.error.issues) {
        const field = issue.path[0] as string;
        fieldErrors[field] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }

    register.mutate(result.data);
  }

  return (
    <div className="flex min-h-screen bg-surface-50">
      {/* Left — Brand panel */}
      <div className="hidden lg:flex lg:w-[480px] bg-brand-600 flex-col justify-between p-12">
        <div className="flex items-center">
          <img src="/assets/logo.png" alt="Eriko" className="h-8 w-auto brightness-0 invert" />
        </div>

        <div>
          <h1 className="text-3xl font-bold text-white leading-tight tracking-tight whitespace-pre-wrap">
            {t('register.tagline')}
          </h1>
          <p className="mt-4 text-brand-100 text-[15px] leading-relaxed max-w-sm">
            {t('register.description')}
          </p>
        </div>

        <p className="text-brand-200 text-xs">{t('copyright', { ns: 'common' })}</p>
      </div>

      {/* Right — Register form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {/* Mobile brand */}
          <div className="lg:hidden flex items-center mb-10">
            <img src="/assets/logo.png" alt="Eriko" className="h-8 w-auto" />
          </div>

          <h2 className="text-2xl font-bold text-surface-900 tracking-tight">{t('register.title')}</h2>
          <p className="mt-1.5 text-sm text-surface-500">
            {t('register.subtitle')}
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="fullName" className="text-sm font-medium text-surface-700">
                {t('register.fullName')}
              </Label>
              <Input
                id="fullName"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder={t('register.fullNamePlaceholder')}
                className="mt-1.5"
                autoComplete="name"
                autoFocus
              />
              {errors.fullName && (
                <p className="mt-1 text-xs text-red-500">{errors.fullName}</p>
              )}
            </div>

            <div>
              <Label htmlFor="email" className="text-sm font-medium text-surface-700">
                {t('register.email')}
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-1.5"
                autoComplete="email"
              />
              {errors.email && (
                <p className="mt-1 text-xs text-red-500">{errors.email}</p>
              )}
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-medium text-surface-700">
                {t('register.password')}
              </Label>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('register.passwordPlaceholder')}
                  className="pr-10"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-400 hover:text-surface-600"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" strokeWidth={1.8} />
                  ) : (
                    <Eye className="w-4 h-4" strokeWidth={1.8} />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-xs text-red-500">{errors.password}</p>
              )}
            </div>

            <div>
              <Label htmlFor="orgName" className="text-sm font-medium text-surface-700">
                {t('register.orgName')}
              </Label>
              <Input
                id="orgName"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                placeholder={t('register.orgNamePlaceholder')}
                className="mt-1.5"
              />
              {errors.orgName && (
                <p className="mt-1 text-xs text-red-500">{errors.orgName}</p>
              )}
            </div>

            <div>
              <Label htmlFor="jurisdiction" className="text-sm font-medium text-surface-700">
                {t('register.country', 'Country / Jurisdiction')}
              </Label>
              <Select value={jurisdiction} onValueChange={(v) => setJurisdiction(v as 'US' | 'UK')}>
                <SelectTrigger id="jurisdiction" className="mt-1.5">
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="US">United States</SelectItem>
                  <SelectItem value="UK">United Kingdom</SelectItem>
                </SelectContent>
              </Select>
              {errors.jurisdiction && (
                <p className="mt-1 text-xs text-red-500">{errors.jurisdiction}</p>
              )}
            </div>

            {register.error && (
              <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2.5">
                <p className="text-sm text-red-700">
                  {(register.error as Error).message === 'EMAIL_EXISTS'
                    ? t('register.emailExists')
                    : (register.error as Error).message}
                </p>
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-10 text-sm font-semibold"
              disabled={register.isPending}
            >
              {register.isPending ? t('register.submitting') : t('register.submit')}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-surface-500">
            {t('register.hasAccount')}{' '}
            <Link to="/login" className="font-semibold text-brand-600 hover:text-brand-700">
              {t('register.signIn')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
